"use client"

import { AppDialog, type AppDialogProps, type DialogAction } from "./app-dialog"

type Controlled<T> = Omit<T, "variant" | "open" | "onOpenChange" | "defaultOpen" | "trigger"> & {
  open: boolean
  onOpenChange: (open: boolean) => void
  variant?: never
  defaultOpen?: never
  trigger?: never
  className?: never
  style?: never
  width?: never
  size?: never
  header?: never
  footer?: never
  bodyClassName?: never
  headerClassName?: never
  footerClassName?: never
  customClassName?: never
  overlayClassName?: never
  contentProps?: never
}

type FormShell = Controlled<Extract<AppDialogProps, { variant: "simpleForm" | "gridForm" }>>
type FormAction = Omit<DialogAction, "onClick" | "closeAfterClick"> & {
  form: string
  onClick?: never
  closeAfterClick?: never
}
type ClickAction = Omit<DialogAction, "form"> & {
  onClick: NonNullable<DialogAction["onClick"]>
  form?: never
}

/** Submit the caller's existing form by ID; the dialog never creates a form or another submit handler. Omit the action on a step with nothing to submit. */
export type SimpleFormDialogProps = Omit<FormShell, "action"> & { action?: FormAction }
export type GridFormDialogProps = SimpleFormDialogProps
export type FullscreenDialogProps = Controlled<Extract<AppDialogProps, { variant: "fullscreen" }>>
type ControlledInformation = Controlled<Extract<AppDialogProps, { variant: "information" }>>
export type InformationDialogProps =
  | ControlledInformation
  | (Omit<ControlledInformation, "onOpenChange" | "dismiss"> & { dismiss: "none"; onOpenChange?: never })
export type GenericDialogProps = Omit<
  Controlled<Extract<AppDialogProps, { variant: "generic" }>>,
  "action" | "secondaryAction"
> & { action?: ClickAction; secondaryAction?: ClickAction }

type ConfirmationProps<T> = Omit<Controlled<T>, "action" | "cancel" | "logo" | "navigation" | "dismiss"> & {
  action: ClickAction
  cancel?: { disabled?: boolean; onClick?: () => void }
  dismiss?: "explicit" | "none"
}
export type ConfirmDialogProps = ConfirmationProps<Extract<AppDialogProps, { variant: "alert" }>>
export type DestroyDialogProps = ConfirmationProps<Extract<AppDialogProps, { variant: "destroy" }>>

/** Public components select a design; all modal behavior and shell rendering stay in AppDialog. */
export function SimpleFormDialog(props: SimpleFormDialogProps) {
  return <AppDialog {...props} variant="simpleForm" />
}

export function GridFormDialog(props: GridFormDialogProps) {
  return <AppDialog {...props} variant="gridForm" />
}

export function InformationDialog(props: InformationDialogProps) {
  return <AppDialog {...props} variant="information" />
}

export function GenericDialog(props: GenericDialogProps) {
  return <AppDialog {...props} variant="generic" />
}

export function ConfirmDialog(props: ConfirmDialogProps) {
  return <AppDialog {...props} variant="alert" />
}

export function DestroyDialog(props: DestroyDialogProps) {
  return <AppDialog {...props} variant="destroy" />
}

export function FullscreenDialog(props: FullscreenDialogProps) {
  return <AppDialog {...props} variant="fullscreen" />
}
